/**
 * PropitstopDrive — /business/drive marketing page dedicated to the
 * Dealer platform ("Propitstop Drive"). Layout follows the reference
 * mockup shared by the product owner (Feb 24, 2026) with the pricing
 * section intentionally omitted per instructions.
 *
 * Structure:
 *   1. Slim brand nav (logo + Book a Demo)
 *   2. Hero (headline + CTAs + trust stats + product mockup)
 *   3. "Trusted by growing dealerships" strip
 *   4. Feature grid — 8 tiles ("One Platform. Everything You Need.")
 *   5. Split — "Your Website Comes Free" + Customer Journey timeline
 *   6. Without vs With comparison
 *   7. Final CTA
 *   8. Footer
 *
 * Note: this page uses its own local top nav (not the umbrella
 * `PropitstopNav`) because it's the dedicated dealer product story and
 * needs different tone/branding (red car logo, black nav).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, Car, Users, MessageCircle, Sparkles, LineChart, FileText, Send,
  Search, ArrowRight, ArrowRightCircle, Calendar, Building2, Users2, ShieldCheck,
  Eye, UsersRound, Handshake, CheckCircle2, XCircle, PhoneCall,
} from 'lucide-react';
import PropitstopFooter from '../components/PropitstopFooter';
import SEO from '../components/SEO';
import DemoBookingModal from '../components/DemoBookingModal';
import { useSiteImages } from '../hooks/useSiteImages';

const DRIVE_LOGO =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_fcf8fe40-6235-4ae1-a5a5-ebd4002e0a55/artifacts/wo8qcjma_ChatGPT%20Image%20May%2010%2C%202026%2C%2011_55_27%20PM.png';

// Product mockup image on the hero (laptop + phone showing a dealer microsite).
const HERO_MOCKUP =
  'https://images.unsplash.com/photo-1618477388954-7852f32655ec?auto=format&fit=crop&w=1400&q=80';

// Default artwork for the mid-page "AI-Powered Inventory + Customer Journey"
// banner. Admin can override via Site Images → drive_website_free_banner.
const WEBSITE_JOURNEY_BANNER_DEFAULT =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_fcf8fe40-6235-4ae1-a5a5-ebd4002e0a55/artifacts/u6xn0rqm_ChatGPT%20Image%20Aug%209%2C%202026%2C%2001_20_28%20AM.png';

const HERO_STATS = [
  { icon: Building2, value: '500+', label: 'Dealerships Interacted' },
  { icon: Users2, value: '1000+', label: 'Automotive Professionals' },
  { icon: ShieldCheck, value: 'Built by', label: 'Automotive Experts' },
];

const TRUST_STRIP = [
  { icon: Eye, label: 'Increase Visibility' },
  { icon: UsersRound, label: 'Generate More Leads' },
  { icon: Handshake, label: 'Build Customer Trust' },
  { icon: LineChart, label: 'Grow Repeat Business' },
];

const FEATURES = [
  { icon: Globe, title: 'Dealer Website', desc: 'Get your own professional website with your brand and domain.', accent: 'text-blue-600 bg-blue-50' },
  { icon: Car, title: 'Inventory Management', desc: 'Manage all your vehicles, images, pricing and documents in one place.', accent: 'text-emerald-600 bg-emerald-50' },
  { icon: Users, title: 'Lead Management', desc: 'Capture enquiries from your website, social media and other channels.', accent: 'text-orange-600 bg-orange-50' },
  { icon: UsersRound, title: 'Customer CRM', desc: 'Manage customers, follow-ups, history and relationships that last.', accent: 'text-sky-600 bg-sky-50' },
  { icon: Sparkles, title: 'AI Content Assistant', desc: 'Generate car descriptions, social posts, and ads in seconds.', accent: 'text-violet-600 bg-violet-50' },
  { icon: LineChart, title: 'Analytics & Reports', desc: 'Track performance, enquiries, popular cars and sales insights.', accent: 'text-pink-600 bg-pink-50' },
  { icon: FileText, title: 'Finance & Documents', desc: 'Store important documents, finance details and agreements securely.', accent: 'text-teal-600 bg-teal-50' },
  { icon: Send, title: 'WhatsApp Integration', desc: 'Connect WhatsApp Business and respond to leads faster.', accent: 'text-green-600 bg-green-50' },
];

const JOURNEY = [
  { icon: Search, label: 'Customer Searches' },
  { icon: Globe, label: 'Finds Your Website' },
  { icon: Car, label: 'Views Inventory' },
  { icon: MessageCircle, label: 'Sends Enquiry' },
  { icon: PhoneCall, label: 'You Follow Up in CRM' },
  { icon: CheckCircle2, label: 'Close the Sale' },
];

const WITHOUT = [
  'Depend on classifieds & marketplaces',
  'No ownership of customer data',
  'Lose enquiries & follow-ups',
  'No brand visibility',
  'Manual & scattered processes',
];

const WITH = [
  'Your own professional website',
  'Capture & manage all enquiries',
  'Build strong customer relationships',
  'Grow your brand and trust',
  'Everything in one platform',
];

export default function PropitstopDrive() {
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);
  const { imageFor } = useSiteImages();
  const heroMockup = imageFor('drive_hero_mockup', HERO_MOCKUP);
  const websiteJourneyBanner = imageFor('drive_website_free_banner', WEBSITE_JOURNEY_BANNER_DEFAULT);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="propitstop-drive-page">
      <DemoBookingModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        source="drive"
        accent="bg-orange-500 hover:bg-orange-600"
        title="Tell us about your dealership"
      />
      <SEO
        title="AI-Powered Used Car Dealer Management Platform — Website in Under 30 Minutes | Propitstop Drive"
        description="Propitstop Drive is India's AI-powered used car dealer management platform. Get your own dealer website ready in under 30 minutes, capture leads, manage inventory and grow your dealership without depending on aggregators."
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/business/drive` : ''}
      />

      {/* ============================== NAV ============================== */}
      <nav data-testid="drive-nav" className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/business/drive" data-testid="drive-nav-logo" className="flex items-center gap-2">
            <img src={DRIVE_LOGO} alt="Propitstop Drive" className="h-10 w-10 rounded-lg object-cover" />
            <div className="leading-tight">
              <div className="font-extrabold text-lg text-zinc-900 tracking-tight">propitstop</div>
              <div className="text-[9px] uppercase tracking-[0.28em] text-orange-500 font-bold -mt-0.5">DRIVE</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-zinc-700">
            <a href="#features" className="hover:text-zinc-900">Features</a>
            <a href="#journey" className="hover:text-zinc-900">How it works</a>
            <a href="#compare" className="hover:text-zinc-900">Why Drive</a>
            <a href="#contact" className="hover:text-zinc-900">Contact</a>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/dealers/login"
              data-testid="drive-signin"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-700 hover:text-zinc-900"
            >
              Sign In
            </Link>
            <button
              type="button"
              onClick={openDemo}
              data-testid="drive-book-demo-nav"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold transition-colors"
            >
              <Calendar className="w-4 h-4" /> Book Demo
            </button>
          </div>
        </div>
      </nav>

      {/* ============================== HERO ============================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 grid lg:grid-cols-[1fr,1.35fr] gap-8 lg:gap-14 items-center">
        <div>
          <span className="inline-block px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-[11px] font-bold uppercase tracking-widest">
            Built for Used Car Dealers
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-zinc-900">
            Sell More Cars.<br />Grow Your Dealership<br />
            <span className="text-orange-500">Digitally.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-500 max-w-xl leading-relaxed">
            Your complete digital operating platform to build your brand, attract more buyers and manage your business smarter.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={openDemo}
              data-testid="drive-book-demo-hero"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold transition-colors shadow-lg shadow-orange-500/25"
            >
              <Calendar className="w-4 h-4" /> Book a Demo
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {HERO_STATS.map((s) => (
              <div key={s.label} className="flex items-start gap-2">
                <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <div className="text-lg font-extrabold text-zinc-900 leading-none">{s.value}</div>
                  <div className="text-[11px] text-zinc-500 leading-tight mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hero image mockup — larger cinematic frame; floating stat cards removed per product decision */}
        <div className="relative">
          <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-br from-orange-100 via-white to-blue-100 rounded-[36px] blur-xl opacity-70" />
          <div className="relative rounded-[28px] bg-gradient-to-br from-orange-50 via-white to-blue-50 p-2 sm:p-3 border border-white/70 shadow-2xl shadow-orange-200/50">
            <img
              src={heroMockup}
              alt="Propitstop Drive dealer platform preview"
              fetchPriority="high"
              decoding="async"
              className="w-full aspect-[16/10] object-contain bg-white rounded-[22px]"
              data-testid="drive-hero-image"
            />
          </div>
        </div>
      </section>

      {/* =========================== TRUST STRIP ========================== */}
      <section className="bg-zinc-50 border-y border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm font-semibold text-zinc-600 mb-6">Trusted by Growing Dealerships Across India</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TRUST_STRIP.map((t) => (
              <div key={t.label} className="flex items-center justify-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white border border-zinc-200 flex items-center justify-center">
                  <t.icon className="w-4 h-4 text-orange-500" />
                </div>
                <div className="text-sm font-semibold text-zinc-800">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ FEATURES ============================ */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900">One Platform. Everything You Need.</h2>
          <p className="mt-3 text-sm text-zinc-500">All the tools you need to run and grow your dealership — under one roof.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              data-testid={`drive-feature-${f.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              className="p-5 rounded-2xl bg-white border border-zinc-200 hover:border-orange-300 hover:shadow-lg transition-all"
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${f.accent}`}>
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 text-base font-extrabold text-zinc-900">{f.title}</h3>
              <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* =========== AI-POWERED INVENTORY + CUSTOMER JOURNEY BANNER =========
          Single hero banner (design owned by product). Admin can swap the
          artwork via Site Images → "Drive — Website/Journey Banner". */}
      <section id="journey" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="rounded-3xl overflow-hidden border border-zinc-200 bg-white shadow-sm">
          <img
            src={websiteJourneyBanner}
            alt="AI-Powered Inventory. Automated Website. Grow Your Sales. — Customer journey from discovery to delivery."
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-contain bg-white"
            data-testid="drive-website-journey-banner"
          />
        </div>
      </section>

      {/* =========================== COMPARISON =========================== */}
      <section id="compare" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900">Why Dealers Choose Propitstop Drive</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5 lg:gap-6 relative">
          <div className="p-6 lg:p-8 rounded-3xl bg-zinc-50 border border-zinc-200">
            <div className="text-lg font-extrabold text-zinc-800">Without Propitstop Drive</div>
            <ul className="mt-4 space-y-2.5 text-sm text-zinc-700">
              {WITHOUT.map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-1.5">
              {['OLX', 'Calls', 'WhatsApp', 'Excel Sheets'].map((c) => (
                <span key={c} className="px-2.5 py-1 rounded-full bg-white text-xs font-semibold text-zinc-500 border border-zinc-200">{c}</span>
              ))}
            </div>
          </div>

          {/* VS badge */}
          <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-orange-500 text-white font-extrabold text-lg items-center justify-center shadow-xl shadow-orange-500/40">VS</div>

          <div className="p-6 lg:p-8 rounded-3xl bg-emerald-50/40 border border-emerald-200">
            <div className="text-lg font-extrabold text-emerald-700">With Propitstop Drive</div>
            <ul className="mt-4 space-y-2.5 text-sm text-zinc-700">
              {WITH.map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============================ FINAL CTA ========================== */}
      <section id="contact" className="bg-gradient-to-r from-orange-100 via-orange-50 to-white border-t border-orange-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-zinc-900">Ready to Grow Your Dealership?</h3>
            <p className="mt-1 text-sm text-zinc-600 max-w-xl">
              Book a personalised demo and see how Propitstop Drive can help you sell more cars and grow your business.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={openDemo}
              data-testid="drive-cta-book"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold transition-colors shadow-lg shadow-orange-500/25"
            >
              <Calendar className="w-4 h-4" /> Book a Demo
            </button>
          </div>
        </div>
      </section>

      <PropitstopFooter />
    </div>
  );
}
